'use client';

import { useQuery } from '@tanstack/react-query';
import { useLogger } from 'next-axiom';
import { ELEVAR_CUSTOMER_QUERY_KEY } from './elevar-constants';
import { checkIfSiteHasElevar } from './elevar-helpers';
import type { ElevarUserProperties } from './elevar-types';

export function useElevarCustomer() {
  const logger = useLogger();

  return useQuery({
    queryKey: [ELEVAR_CUSTOMER_QUERY_KEY],
    queryFn: () => getElevarCustomer(logger),
    retry: (failureCount, error) => {
      // Only retry network errors, not 4xx errors
      if (error instanceof Error && error.message.includes('4')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

async function getElevarCustomer(
  logger: ReturnType<typeof useLogger>
): Promise<ElevarUserProperties> {
  try {
    if (!checkIfSiteHasElevar()) {
      return {
        visitor_type: 'guest',
      };
    }

    const response = await fetch('/api/elevar/customer', {
      signal: AbortSignal.timeout(5000), // 5 second timeout
    });

    if (!response.ok) {
      logger.warn('Elevar customer fetch failed', {
        status: response.status,
        statusText: response.statusText,
        url: '/api/elevar/customer',
        component: 'useElevarCustomer',
      });

      return {
        visitor_type: 'guest',
      };
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Elevar customer fetch error', {
      error: error instanceof Error ? error.message : String(error),
      component: 'useElevarCustomer',
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });

    // Return default guest properties on error
    return {
      visitor_type: 'guest',
    };
  }
}
